/**
 * LocalStorageBackend.ts — `window.localStorage`-backed StorageBackend.
 *
 * Storage layout:
 *   bs.prefs.<key>     JSON-encoded pref value
 *   bs.bundle.<id>     JSON-encoded ScenarioBundle
 *   bs.ids             JSON-encoded string[] — the canonical list of saved
 *                      bundle ids (maintained so listIds() doesn't have to scan
 *                      every localStorage key, which is O(n) over all keys).
 *
 * All writes are wrapped in try/catch — quota / private-mode failures degrade
 * gracefully (the write is dropped, the app keeps working in-memory).
 */
import type { ScenarioBundle } from '../engine/types';
import type { StorageBackend } from './StorageBackend';

const PREFS_PREFIX = 'bs.prefs.';
const BUNDLE_PREFIX = 'bs.bundle.';
const IDS_KEY = 'bs.ids';

export class LocalStorageBackend implements StorageBackend {
  private readonly ls: Storage;

  constructor(ls: Storage = window.localStorage) {
    this.ls = ls;
  }

  // ── Prefs ────────────────────────────────────────────────────────────────
  get<T>(key: string, fallback: T): T {
    try {
      const raw = this.ls.getItem(PREFS_PREFIX + key);
      if (raw === null) return fallback;
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      this.ls.setItem(PREFS_PREFIX + key, JSON.stringify(value));
    } catch {
      // Quota exceeded / private mode / disabled storage — degrade silently.
    }
  }

  delete(key: string): void {
    try {
      this.ls.removeItem(PREFS_PREFIX + key);
    } catch {
      // ignore
    }
  }

  // ── Saved scenarios ──────────────────────────────────────────────────────
  listIds(): string[] {
    try {
      const raw = this.ls.getItem(IDS_KEY);
      if (raw === null) return [];
      const ids = JSON.parse(raw);
      return Array.isArray(ids) ? ids.filter((x) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }

  saveBundle(bundle: ScenarioBundle): void {
    try {
      this.ls.setItem(BUNDLE_PREFIX + bundle.id, JSON.stringify(bundle));
      // Maintain the id list (append if new).
      const ids = new Set(this.listIds());
      ids.add(bundle.id);
      this.ls.setItem(IDS_KEY, JSON.stringify([...ids]));
    } catch {
      // degrade silently
    }
  }

  loadBundle(id: string): ScenarioBundle | null {
    try {
      const raw = this.ls.getItem(BUNDLE_PREFIX + id);
      if (raw === null) return null;
      return JSON.parse(raw) as ScenarioBundle;
    } catch {
      return null;
    }
  }

  deleteBundle(id: string): void {
    try {
      this.ls.removeItem(BUNDLE_PREFIX + id);
      const ids = new Set(this.listIds());
      ids.delete(id);
      this.ls.setItem(IDS_KEY, JSON.stringify([...ids]));
    } catch {
      // ignore
    }
  }
}

/** In-memory fallback used when localStorage isn't available. Behaves like
 *  LocalStorageBackend but nothing persists across reloads. */
export class MemoryBackend implements StorageBackend {
  private prefs = new Map<string, unknown>();
  private bundles = new Map<string, ScenarioBundle>();

  get<T>(key: string, fallback: T): T {
    return (this.prefs.get(key) as T) ?? fallback;
  }
  set<T>(key: string, value: T): void {
    this.prefs.set(key, value);
  }
  delete(key: string): void {
    this.prefs.delete(key);
  }
  listIds(): string[] {
    return [...this.bundles.keys()];
  }
  saveBundle(bundle: ScenarioBundle): void {
    this.bundles.set(bundle.id, bundle);
  }
  loadBundle(id: string): ScenarioBundle | null {
    return this.bundles.get(id) ?? null;
  }
  deleteBundle(id: string): void {
    this.bundles.delete(id);
  }
}

/** Pick the right backend for the current environment. localStorage when
 *  available, MemoryBackend otherwise. */
export function getDefaultStorage(): StorageBackend {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Probe localStorage is actually writable (Safari private mode throws).
      const probe = '__bs_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return new LocalStorageBackend(window.localStorage);
    }
  } catch {
    // fall through
  }
  return new MemoryBackend();
}
